import { useState } from 'react';
import { Alert, Button, Card, Form, Input, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { changeAccountPassword } from '../api/access';
import { useCapabilities } from '../access/useCapabilities';
import { getDashboard, type PAAccount, type PADashboard } from '../api/money';
import { MoneyPage, QueryFrame } from '../money/MoneyPage';
import { usePAQuery } from '../money/usePAQuery';
import { errorText } from '../session/errorText';

/**
 * ============================================================================
 * «ПАРОЛЬ УЧЁТНОЙ ЗАПИСИ» — the ENROLLMENT credential (§11, F22)
 * ============================================================================
 *
 * ⚠ THIS IS THE ONLY SELF-SERVICE ROUTE TO AN ACCOUNT PASSWORD, and it is a DIFFERENT
 * credential from the one «Изменение пароля» changes. The Account password is what the
 * ENROLLMENT portal asks for when a device is bound; password recovery rotates
 * `subscriber.password` — the Personal-Area credential — and never touches this one. Before
 * this screen a subscriber who had forgotten it had exactly one option: the front desk.
 *
 * That is also why it is its own nav section rather than a second card on the portal-password
 * screen. A subscriber holds TWO credentials (P7-A1, which the login screen already carries a
 * hint about), and two forms on one page — one of them asking for «текущий пароль» — is an
 * invitation to type the portal password into this one and be told it is wrong.
 *
 * # Four rules
 *
 *  1. **THE CAPABILITY GATES THE FORM.** `account_password_change_enabled` is an operator
 *     switch and the write route enforces it (`409 pa.password_change_disabled`), so a form
 *     rendered against a `false` capability is a dead control. A FAILED capability read is
 *     the error frame with «Повторить» and NOT «отключено»: telling a subscriber his
 *     operator has switched something off because a read timed out is a support call and a
 *     falsehood.
 *  2. **C18 IS NOT RE-IMPLEMENTED HERE.** The character set lives in `internal/users` and
 *     arrives as `400 pa.password_charset` with a Russian message this screen shows
 *     verbatim. A second copy would drift, and the copy that drifts is the one the
 *     subscriber believes. There is no charset HINT either, for the same reason —
 *     `PasswordScreen.test.tsx` scans every source file in this app and fails if one
 *     appears.
 *  3. **THE CONFIRMATION NEVER LEAVES THE BROWSER.** The wire takes `current_password` and
 *     `new_password` and nothing else: a server that receives two copies of a new password
 *     can only compare them, which the browser already did. If the browser does NOT compare
 *     them, the password silently becomes a typo and the screen reports success.
 *  4. **NO PASSWORD IS EVER DISPLAYED.** The column is recoverable by deliberate policy, so
 *     putting it on this internet-facing listener would be a live credential on the wire.
 *     §11's «show/hide (eye) toggle» applies to what he is TYPING — `Input.Password` has
 *     one — and to nothing that was read back from the server, because nothing is.
 *
 * # The honest consequence: none
 *
 * RADIUS does not check the Account password (the `User-Password` carries a per-NAS filler
 * and is a shared-secret proof), so live sessions are MAC/IP-bound and unaffected and only
 * the NEXT enrollment asks for the new value. The success panel says so, because a
 * subscriber who expected his connection to drop and watched it not drop assumes the change
 * did not take.
 */
export function AccountPasswordScreen() {
  return (
    <MoneyPage titleKey="accountPassword.title" leadKey="accountPassword.lead">
      {(contract) => <AccountPasswordBody contractID={contract.id} />}
    </MoneyPage>
  );
}

function AccountPasswordBody({ contractID }: { contractID: number }) {
  const { t } = useTranslation();
  const caps = useCapabilities();
  // The account list, from this SPA's one canonical per-contract read — see
  // `DevicesScreen`'s note on the same choice.
  const accounts = usePAQuery<PADashboard>(() => getDashboard(contractID), [contractID]);

  return (
    <QueryFrame query={caps.query} reload={caps.reload}>
      {(capabilities) => {
        // RULE 1: the switch decides whether the form exists at all.
        if (!capabilities.account_password_change_enabled) {
          return <Alert type="info" showIcon message={t('accountPassword.disabled')} />;
        }
        return (
          <QueryFrame query={accounts.query} reload={accounts.reload}>
            {(dashboard) =>
              dashboard.accounts.length === 0 ? (
                <Alert type="info" showIcon message={t('accountPassword.noAccounts')} />
              ) : (
                <PasswordForm contractID={contractID} accounts={dashboard.accounts} />
              )
            }
          </QueryFrame>
        );
      }}
    </QueryFrame>
  );
}

function PasswordForm({
  contractID,
  accounts,
}: {
  contractID: number;
  accounts: PAAccount[];
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [chosen, setChosen] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);

  const account = accounts.find((a) => a.account_id === chosen) ?? accounts[0];

  const submit = async (values: { current: string; next: string }) => {
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await changeAccountPassword(contractID, account.account_id, values.current, values.next);
      setDone(true);
      form.resetFields();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="pa-block">
      {done ? (
        <Alert
          type="success"
          showIcon
          message={t('accountPassword.success')}
          description={t('accountPassword.successNote')}
          style={{ marginBottom: 20 }}
        />
      ) : null}
      {error ? (
        <Alert type="error" showIcon message={errorText(error, t)} style={{ marginBottom: 20 }} />
      ) : null}

      {/* ⚠ NO PICKER FOR A SINGLE ACCOUNT — the dead-control rule this app applies to the
          contract chooser and the device chooser. With one account its login is a FACT
          below, so the form still says whose password is being changed. */}
      {accounts.length > 1 ? (
        <div className="pa-picker">
          <label className="pa-picker-label" htmlFor="pa-account-password-account">
            {t('accountPassword.accountLabel')}
          </label>
          <Select<number>
            id="pa-account-password-account"
            className="pa-picker-select"
            value={account.account_id}
            onChange={(id) => {
              // The old password belongs to the account it was typed for: keeping it across
              // a switch would send one account's credential to another and count a failed
              // guess against the lockout.
              form.resetFields();
              setDone(false);
              setError(null);
              setChosen(id);
            }}
            options={accounts.map((a) => ({ value: a.account_id, label: a.login }))}
          />
        </div>
      ) : null}

      <span className="pa-field-label">{t('accountPassword.accountLabel')}</span>
      <p className="pa-contact-value">
        <span className="mono">{account.login}</span>
      </p>

      <Form form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={busy}>
        <Form.Item
          name="current"
          label={t('accountPassword.currentLabel')}
          extra={t('accountPassword.currentHint')}
          rules={[{ required: true, message: t('common.required') }]}
        >
          <Input.Password autoComplete="off" size="large" />
        </Form.Item>

        <Form.Item
          name="next"
          label={t('accountPassword.newLabel')}
          rules={[{ required: true, message: t('common.required') }]}
        >
          {/* No charset rule and no charset hint. RULE 2 — C18 lives in the server. */}
          <Input.Password autoComplete="new-password" size="large" />
        </Form.Item>

        <Form.Item
          name="confirm"
          label={t('accountPassword.confirmLabel')}
          dependencies={['next']}
          rules={[
            { required: true, message: t('common.required') },
            // RULE 3: the ONE client-side rule in this form, and it is not a copy of a
            // server rule — the server never sees this field, so if the browser does not
            // compare the two, nothing does.
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                !value || getFieldValue('next') === value
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('accountPassword.mismatch'))),
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" size="large" />
        </Form.Item>

        <Button type="primary" htmlType="submit" size="large" block loading={busy}>
          {t('accountPassword.submit')}
        </Button>
      </Form>
    </Card>
  );
}
